import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import type { Stripe } from "stripe";
import { normalizeServiceType, ServiceType } from "src/services/subscription/plan-service-type.enum";
import { OrderApiService } from "src/services/order/order-api.service";
import { AuthService } from "src/services/auth/auth.service";
import { CheckoutSession } from "src/services/payment/payment.interface";
import { ProductBought } from "../../app/redesign/customer/thank-you/thank-you.page";
import { redesignPages } from "src/services/navigation/paths.const";

import { apiPath, paymentEndpoint } from "../../shared/api-path.const";
import { BackendBody, OrderFees } from "../../shared/backend-api.types";
import { assertNever } from "src/services/utils/enum.utils";
import { ApiService } from "../http/api.service";
import { HttpService } from "../http/http.service";

@Injectable({
  providedIn: "root",
})
export class PaymentsService {
  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly httpService: HttpService,
    private readonly orderService: OrderApiService,
  ) {}

  public async checkIfPaymentRequired(
    itemIds: string[],
    serviceType: ServiceType | string,
  ): Promise<BackendBody["PaymentRequirement"]> {
    const normalizedServiceType = normalizeServiceType(serviceType);
    return this.apiService.sendPostRequest(apiPath.paymentRequirement, { itemIds, serviceType: normalizedServiceType });
  }

  public async checkIfPaymentRequiredForPickupOnly(
    serviceType: ServiceType | string,
  ): Promise<BackendBody["PaymentRequirement"]> {
    const normalizedServiceType = normalizeServiceType(serviceType);
    return this.apiService.sendPostRequest(`${apiPath.paymentRequirement}/${paymentEndpoint.forPickupOnly}`, {
      serviceType: normalizedServiceType,
    });
  }

  public createStripeCheckoutSessionForOrder(
    userId: string,
    pickupOrderId: string,
    deliveryOrderId?: string,
  ): Promise<CheckoutSession> {
    const productBought: ProductBought = "delivery";
    return this.apiService.sendPostRequestWithLoading<BackendBody["OrderCheckoutSessionRequestBody"], CheckoutSession>(
      apiPath.orderCheckoutSession,
      {
        deliveryOrderId: deliveryOrderId,
        pickupOrderId: pickupOrderId,
        successRedirectUrl: `${environment.endpoints.frontendDomain}/${redesignPages.thankYou}/${productBought}`,
        userId,
      },
    );
  }

  public createStripePaymentSessionForSubscription(
    userId: string,
    successRedirectUrl: string,
    stripeSubscriptionId: string,
    coupon?: string,
    additionalOrderIdFromKiosk?: string,
  ): Promise<CheckoutSession> {
    return this.apiService.sendPostRequestWithLoading<
      BackendBody["SubscriptionCheckoutSessionRequestBody"],
      CheckoutSession
    >(apiPath.subscriptionCheckoutSession, {
      coupon,
      item: { price: stripeSubscriptionId },
      orderIdFromKiosk: additionalOrderIdFromKiosk,
      successRedirectUrl,
      userId,
    });
  }

  public async getPaymentRequiredReasonDisplayString(
    reason: BackendBody["PaymentRequirement"]["reason"],
    fees: OrderFees,
  ): Promise<string | undefined> {
    switch (reason) {
      case "freeFirstTimePickupAlwaysFree":
      case "freeLocalDeliveriesLimit": {
        const limitString =
          fees.freeLocalDeliveriesPerSeason === 1 ? "1 delivery" : `${fees.freeLocalDeliveriesPerSeason} deliveries`;

        return `You are entitled for ${limitString} per season and our records show that you already scheduled ${await this.getNumberOfLocalDeliveriesForLastSeason()} this season.`;
      }
      case "freeLocalPickupsLimit": {
        const limitString =
          fees.freeLocalPickupsPerSeason === 1 ? "1 pickup" : `${fees.freeLocalPickupsPerSeason} pickups`;

        return `You are entitled for ${limitString} per season and our records show that you already scheduled ${await this.getNumberOfLocalPickupsForLastSeason()} this season.`;
      }
      case "noFees":
      case "freeOrderIncluded":
        return "Included in your plan at no additional cost.";
      case "nationwideAlwaysRequirePayment":
        return "Orders outside of our serviced areas require additional payment.";
      case "outsideUserBaseAlwaysRequirePayment":
        return "This order is outside your base location. Only deliveries in your selected base location are included in your plan.";
      case "subscriptionTypeAlwaysRequirePayment":
        return "Your plan does not provide free orders. If you think that this is a mistake, contact us.";
      case "noActiveSubscription":
      case "noSubscription":
        return "No subscription is linked to your account. If you think that this is a mistake, contact us.";
      default:
        return assertNever(reason);
    }
  }

  public async getSubscriptions(): Promise<unknown> {
    return this.httpService.sendGetRequest(environment.endpoints.getSubscriptions);
  }

  public getPaymentHistory(): Promise<Stripe.Charge[]> {
    return this.httpService.sendGetRequest<Stripe.Charge[]>(environment.endpoints.getPaymentHistory);
  }

  public async getUserStripeSubscriptions(userId: string): Promise<Stripe.Subscription[]> {
    const response = await this.apiService.sendGetRequest<BackendBody["StripeSubscriptionsResponse"]>(
      `${apiPath.subscription}/user/${userId}/stripe`,
      undefined,
    );
    return response.subscriptions as Stripe.Subscription[];
  }

  public async setPaymentMethodOnAllSubscriptions(paymentMethodId: string): Promise<void> {
    const activeUserId = await this.requireActiveUserId();
    await this.apiService.sendPutRequest<BackendBody["UpdateAllSubscriptionsArgs"], never>(
      `${apiPath.subscription}/${activeUserId}`,
      {
        stripePaymentMethodId: paymentMethodId,
      },
    );
  }

  private async getNumberOfLocalDeliveriesForLastSeason(): Promise<number> {
    const pickupsThisSeason = (
      await this.orderService.getAllOrdersForUserIdThisSeason(await this.requireActiveUserId())
    )
      .filter((order) => order.serviceType === ServiceType.localDelivery)
      .filter((order) => order.status !== "cancelled");

    return pickupsThisSeason.length;
  }

  private async getNumberOfLocalPickupsForLastSeason(): Promise<number> {
    const pickupsThisSeason = (
      await this.orderService.getAllOrdersForUserIdThisSeason(await this.requireActiveUserId())
    )
      .filter((order) => order.serviceType === ServiceType.localPickup)
      .filter((order) => order.status !== "cancelled");

    return pickupsThisSeason.length;
  }

  private async requireActiveUserId(): Promise<string> {
    const activeUserId = (await this.authService.getActiveUser())?.id;

    if (!activeUserId) {
      throw new Error("No user is logged in.");
    }

    return activeUserId;
  }
}
